// ChoresSection — Feb 2026 (Randy pt89b). Sits at the TOP of HouseholdPanel,
// above "Add Members". Single-row layout mirroring the Reward store, plus
// Property + Pets dropdowns whose values feed the AI Browse Suggestions
// prompt. Chores live in their OWN library (not calendar events); the
// Smart Create Chores Schedule button downstream is what places them on
// the calendar.
//
// pt89b changes vs pt89:
//  - Section header "Set up Chores" above the row.
//  - ChoreEditor: AI icon suggester (LLM one-emoji pick), starker day
//    toggles, no Pattern-through-month button, new Describe field + eye
//    tooltip, Browse suggestions button INSIDE the modal.
//  - ChoreSuggestionsGallery: now a LAYER over ChoreEditor (like the ping
//    gallery layer), X drops back to editor, click-to-fill actually
//    prefills the editor (previous bug: editor was gated on `id`).
//  - Persistence: gallery cards load from backend /chores/suggestions and
//    write back on generate + dismiss → survives log-out / device swap.
import { useEffect, useState, useMemo } from "react";
import { Broom, Sparkle, Plus, PencilSimple, Trash, X, Spinner, Pause, Play, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { runAsyncJob } from "@/lib/asyncJob";
import { useCollapsePref } from "@/lib/uiPrefs";
import InfoTip from "@/components/InfoTip";
import MasterEmojiGeneratorModal from "@/components/MasterEmojiGeneratorModal";
import SimpleDescriberModal from "@/components/SimpleDescriberModal";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

// pt89Y (Feb 20, 2026, Randy) — Property + Pets are Re-Themer-owned now.
// Options come from manifest keys `chores.box1_options` / `chores.box2_options`,
// which live in the silo's `theme_map` when a theme has been applied and fall
// back to the manifest defaults otherwise. The "Explain (write your own)"
// option is HARD-PINNED as the last row inside `parseOptionString` and can
// never be overridden by the Re-Themer.
function parseOptionString(str, explainLabel) {
  const out = [];
  const seen = new Set();
  for (const chunk of (str || "").split(";")) {
    const [rawV, rawL] = chunk.split(":");
    const v = (rawV || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_");
    const l = (rawL || "").trim();
    if (!v || !l || v === "explain" || seen.has(v)) continue;
    seen.add(v);
    out.push({ v, l });
  }
  out.push({ v: "explain", l: explainLabel || "Explain (write your own)" });
  return out;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ---- ChoreIconField — replaces the old ChoreEmojiPicker. One large icon
// slot on the left + a "Find Smart AI icon" search button on the right.
//
// pt89T (Feb 20, 2026, Randy) — the icon slot itself is now a button that
// opens the Master Emoji Generator (batch-of-5 Unicode picker with a
// silo-scoped persistent library + nudge input). Picking an emoji from
// MEG fills this square AND returns to the still-open Create Chore pane
// (preserveParent=true). The existing "Find Smart AI icon" button stays
// as-is — fast, single-emoji shortcut based on the chore name.
function ChoreIconField({ siloId, name, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [megOpen, setMegOpen] = useState(false);
  const suggestIcon = async () => {
    const q = (name || "").trim();
    if (!q) { toast("Add a chore name above first, then tap Smart AI icon.", { id: "chore-icon-noname" }); return; }
    setBusy(true);
    try {
      const r = await api.post(`/calendar/${siloId}/chores/suggest-icon`, { name: q });
      const emoji = (r.data?.emoji || "").trim();
      if (emoji) { onChange(emoji); toast.success(`AI picked ${emoji} for "${q}".`); }
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Couldn't fetch an icon.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => setMegOpen(true)}
        className="w-14 h-14 rounded-xl bg-white border-2 border-dashed border-[#C19A4A]/50 hover:border-[#C19A4A] hover:bg-[#F8F1E2] flex items-center justify-center text-3xl leading-none shadow-inner transition-colors group"
        data-testid="chore-icon-slot"
        aria-label="Open Master Emoji Generator"
        title="Tap to open the Master Emoji Generator (AI batch, silo library, nudge control)"
      >
        {value || "🧹"}
      </button>
      <button type="button" onClick={suggestIcon} disabled={busy || !name?.trim()}
        data-testid="chore-icon-suggest"
        title="AI picks a single icon that matches the chore name (uses your BYOK key)"
        className="text-xs px-3 py-2 rounded-md border border-[#C19A4A] text-[#8A6A24] bg-[#F8F1E2] hover:bg-[#F3E7CB] disabled:opacity-40 flex items-center gap-1.5">
        {busy ? <Spinner size={13} className="animate-spin" /> : <MagicWand size={13} weight="duotone" />}
        Find Smart AI icon
      </button>
      <MasterEmojiGeneratorModal
        siloId={siloId}
        open={megOpen}
        onClose={() => setMegOpen(false)}
        onPick={(emoji) => { onChange(emoji); }}
        preserveParent={true}
        title="Master Emoji Generator"
      />
    </div>
  );
}

// ---- ChoreEditor + nested ChoreSuggestionsGallery layer ----
function ChoreEditor({ siloId, editing, autoOpenGallery, onClose, onSaved, householdMeta }) {
  const tNewChore  = useThemeLabel(T.THEME_KEY_CHORES_NEW_BUTTON);
  const tEditChore = useThemeLabel(T.THEME_KEY_CHORES_EDIT_TITLE);
  const [name, setName] = useState(editing?.name || "");
  const [emoji, setEmoji] = useState(editing?.emoji || "🧹");
  const [effort, setEffort] = useState(editing?.effort_hint || "medium");
  const [days, setDays] = useState(new Set(editing?.suggested_days || []));
  const [tod, setTod] = useState(editing?.suggested_time_of_day || "anytime");
  const [describe, setDescribe] = useState(editing?.describe || "");
  const [busy, setBusy] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(!!autoOpenGallery);
  // Jun 2026 (Randy G6) — empty-description nudge. Descriptions are wiped
  // on a re-theme, so users hit blank ones often. First Save with an empty
  // Describe field shows an in-modal reminder (no toast — Rule 1); a second
  // Save proceeds anyway.
  const [descNudge, setDescNudge] = useState(false);
  // Feb 20, 2026 (pt89T — Randy) — retain the gallery source so a
  // successful chore save can dismiss it from the reserved list. Cancel
  // does NOT dismiss (that's the whole point of the fix). Manually
  // editing the name clears the source association.
  const [sourceSuggestion, setSourceSuggestion] = useState(null);

  const toggleDay = (d) => {
    setDays((cur) => { const n = new Set(cur); n.has(d) ? n.delete(d) : n.add(d); return n; });
  };

  const applySeed = (seed) => {
    if (seed.name) setName(seed.name);
    if (seed.emoji) setEmoji(seed.emoji);
    if (seed.effort_hint) setEffort(seed.effort_hint);
    if (Array.isArray(seed.suggested_days)) setDays(new Set(seed.suggested_days));
    if (seed.suggested_time_of_day) setTod(seed.suggested_time_of_day);
    if (seed.describe) setDescribe(seed.describe);
    if (seed._sourceSuggestion) setSourceSuggestion(seed._sourceSuggestion);
    setGalleryOpen(false);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the chore a name."); return; }
    if (!editing?.id && !describe.trim() && !descNudge) { setDescNudge(true); return; }
    setBusy(true);
    try {
      const body = {
        name: name.trim(), emoji: emoji || "🧹",
        effort_hint: effort,
        suggested_days: Array.from(days),
        suggested_time_of_day: tod,
        describe: describe.trim() || null,
      };
      if (editing?.id) {
        await api.patch(`/calendar/${siloId}/chores/${editing.id}`, body);
      } else {
        await api.post(`/calendar/${siloId}/chores`, body);
      }
      toast.success("Chore saved.");
      // Save-time dismissal — retention fix (pt89T). Fire-and-forget so
      // a delete failure never blocks the successful save.
      if (!editing?.id && sourceSuggestion?.id) {
        try { await api.delete(`/calendar/${siloId}/chores/suggestions/${sourceSuggestion.id}`); }
        catch { /* silent — the gallery will refresh on next open */ }
      }
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[92dvh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()} data-testid="chore-editor">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif-display text-lg">{editing?.id ? tEditChore : tNewChore}</h2>
          <button onClick={() => setGalleryOpen(true)} data-testid="chore-editor-open-gallery"
            className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1">
            <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" /> Browse suggestions
          </button>
        </div>

        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
            placeholder="e.g., Feed the fish" data-testid="chore-name-input" autoFocus
            className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
        </label>

        <div className="mt-3">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Icon</span>
          <div className="mt-1"><ChoreIconField siloId={siloId} name={name} value={emoji} onChange={setEmoji} /></div>
        </div>

        <div className="mt-3">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Effort</span>
          <div className="flex rounded-md border hairline overflow-hidden mt-1">
            {[
              { v: "quick",  l: "Quick (<5 min)" },
              { v: "medium", l: "Medium (5-15)" },
              { v: "long",   l: "Long (15+ min)" },
            ].map((o) => (
              <button key={o.v} type="button" onClick={() => setEffort(o.v)}
                data-testid={`chore-effort-${o.v}`}
                className={`flex-1 text-[11px] px-2 py-1.5 ${effort === o.v ? "bg-[#1C1B1A] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Suggested days (leave blank = AI decides)</span>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {DOW.map((label, i) => {
              const on = days.has(i);
              return (
                <button key={label} type="button" onClick={() => toggleDay(i)}
                  data-testid={`chore-day-${i}`}
                  className={`w-11 py-2 rounded-md text-[11px] font-medium border-2 transition-all ${on
                    ? "bg-[#4A5D4E] text-white border-[#4A5D4E] shadow-md scale-105"
                    : "bg-white text-[#B5B3AE] border-[#E4E1DA] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Suggested time of day</span>
          <div className="flex rounded-md border hairline overflow-hidden mt-1">
            {["morning", "afternoon", "evening", "anytime"].map((o) => (
              <button key={o} type="button" onClick={() => setTod(o)}
                data-testid={`chore-tod-${o}`}
                className={`flex-1 text-[11px] px-2 py-1.5 capitalize ${tod === o ? "bg-[#1C1B1A] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                {o}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] flex items-center gap-1.5">
            Describe
            <InfoTip icon="eye" iconSize={12} placement="bottom-right" width={320} mobileCenter
              testid="chore-describe-eye" label="What should the AI know about this chore?">
              Describe the expectations or details you want the AI to know about this task.{"\n\n"}
              If a child asks the Silo how to complete it to your standard, what would you say?{"\n\n"}
              Example: <em>&quot;Take the compost bin out, rinse it, replace the liner. Standard: bin should smell neutral, not sour. Empty by 7pm on collection day.&quot;</em>
            </InfoTip>
          </div>
          <textarea value={describe} onChange={(e) => setDescribe(e.target.value)}
            placeholder="Optional — the parent's own completion standard. Fed into the Smart Chores Schedule prompt AND used when a kid asks the Silo 'how do I do this?'"
            maxLength={1000} rows={3} data-testid="chore-describe-input"
            className="mt-1 w-full px-3 py-2 border hairline rounded-md text-xs bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E] resize-y leading-relaxed" />
          <div className="text-[9px] text-[#B5B3AE] mt-0.5 text-right tabular-nums">{describe.length}/1000</div>
        </div>

        {descNudge && !describe.trim() && (
          <div data-testid="chore-desc-nudge"
            className="mt-3 text-xs rounded-md border border-[#C19A4A] bg-[#F8F1E2] text-[#6B4E1E] p-2.5 leading-relaxed">
            <strong>No description yet.</strong> A quick note in the Describe box helps the AI schedule this right and answers &quot;how do I do this?&quot; when someone asks. Add one above — or tap Save again to skip.
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="chore-save-btn"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>

        {galleryOpen && (
          <ChoreSuggestionsGalleryLayer
            siloId={siloId} householdMeta={householdMeta}
            existingChores={[]}
            onClose={() => setGalleryOpen(false)}
            onPicked={applySeed}
            onBulkAdded={(added) => {
              // Feb 28 2026 v6 (Randy pt-chore-modal-persist) — bulk-add
              // used to close the entire New Chore pane via
              // `onClose()`, forcing the gallery shut. Randy wants the
              // gallery to STAY OPEN so users can keep picking from
              // the remaining ideas. Just refresh the parent chore list.
              if (added && added.length) {
                onSaved && onSaved();
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

function ChoreSuggestionsGalleryLayer({ siloId, householdMeta, onClose, onPicked, onBulkAdded }) {
  // Feb 2026 (Randy pt89b) — this component now sources its cards from
  // the backend (silo-scoped, per-silo persistent gallery). Local
  // localStorage is only used to remember dismissed items in this browser
  // for filtering follow-up "Suggest more" requests.
  // Jul 30, 2026 (Randy) — gallery title, empty-state and "Suggest more"
  // button are now re-themable per silo. A fitness silo says "Drill ideas",
  // a coaching silo says "Task ideas", a personal home silo keeps "Chore
  // ideas". Every callsite falls back to the manifest default when the silo
  // has no theme_map override.
  const galleryTitle = useThemeLabel(T.THEME_KEY_CHORES_SUGGEST_GALLERY_TITLE);
  const galleryEmpty = useThemeLabel(T.THEME_KEY_CHORES_SUGGEST_GALLERY_EMPTY);
  const suggestMoreLabel = useThemeLabel(T.THEME_KEY_CHORES_SUGGEST_MORE_BUTTON);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // pt89Z (Feb 20, 2026, Randy) — multi-select. Tapping a card toggles it
  // into `selected` (visual highlight ring); the sticky submit bar
  // appears whenever >=1 card is picked and does a bulk-insert with the
  // AI's default fields. Users can fine-tune dates/times later from the
  // Household Setup chore list. If nothing is selected, the FIRST tap
  // still goes straight into the detailed editor via `onPicked` (legacy
  // single-pick flow), so power users can drill into one card fast.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // pt89AA (Feb 20, 2026, Randy) — inline edit for a specific suggestion
  // pill. Save PATCHes the card in-place; Cancel returns to the gallery.
  // Neither commits to the official chore list — that only happens via
  // the sticky "Add N chores" bulk-add bar. Selection state is preserved
  // across Edit sessions so power users can select→edit→select→submit.
  const [editingSug, setEditingSug] = useState(null);
  // Feb 20, 2026 (pt89T — Randy) — same nudge/retention model as the
  // ping gallery. Type an arena and every "Suggest more" batch is biased
  // toward it. Blank ⇒ purely household-context driven.
  const [nudge, setNudge] = useState("");
  const NORM = (s) => (s || "").toLowerCase().split(/\s+/).join(" ");
  const DISMISS_KEY = `mysilo_chore_dismiss_${siloId}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); } catch { return new Set(); }
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/calendar/${siloId}/chores/suggestions`);
        setSuggestions(r.data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally { setLoading(false); }
    })();
  }, [siloId]);

  // pt89T-cap (Feb 20, 2026, Randy) — hard cap the chore-suggestion
  // gallery at 20 per silo (matches ping gallery + emoji library). Cap
  // is enforced BOTH server-side (safer) and client-side (faster UI).
  const CHORE_GALLERY_CAP = 20;
  const choreGalleryFull = suggestions.length >= CHORE_GALLERY_CAP;

  const suggestMore = async () => {
    if (choreGalleryFull) {
      toast.error(`Chore gallery is full (${CHORE_GALLERY_CAP}/${CHORE_GALLERY_CAP}). Delete a card to make room, then try again.`);
      return;
    }
    setBusy(true);
    try {
      const already = [
        ...suggestions.map((s) => s.name),
        ...Array.from(dismissed),
      ];
      // Feb 28 2026 v6 (Randy Chore-Async) — moved off the sync POST
      // (which was clipping Cloudflare's 60s cutoff on ~50% of runs)
      // onto the async-job pattern. Returns {job_id, status:"pending"}
      // immediately, then we poll /calendar/jobs/{id} until done.
      // Same durable pattern that fixed YTRE's timeout issue.
      const result = await runAsyncJob(
        `/calendar/${siloId}/chores/suggestions/generate/async`,
        { already_seen: already, nudge: nudge.trim() || null },
        { pollBase: "/calendar/jobs", timeoutMs: 180000 },
      );
      const seen = new Set(already.map(NORM));
      const fresh = (result.suggestions || []).filter((s) => {
        const k = NORM(s.name);
        if (!k || seen.has(k)) return false;
        seen.add(k); return true;
      });
      if (fresh.length === 0) toast("No new chore ideas this time.", { id: "chore-sugg-none" });
      if (result?.skipped_due_to_cap > 0) {
        toast(`${result.skipped_due_to_cap} idea${result.skipped_due_to_cap === 1 ? "" : "s"} skipped — gallery cap of ${CHORE_GALLERY_CAP} reached.`, { id: "chore-cap-skip" });
      }
      setSuggestions((cur) => [...fresh, ...cur].slice(0, CHORE_GALLERY_CAP));
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || e.message || "Couldn't generate chore ideas.");
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    const k = NORM(s.name);
    setSuggestions((cur) => cur.filter((x) => x.id !== s.id));
    setDismissed((cur) => { const n = new Set(cur); n.add(k); try { localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(n))); } catch { /* quota */ } return n; });
    try { await api.delete(`/calendar/${siloId}/chores/suggestions/${s.id}`); }
    catch { /* silent — the local remove already happened */ }
  };

  const pick = async (s) => {
    // Feb 20, 2026 (pt89T fix — Randy) — DO NOT auto-remove on pick.
    // Previously `await remove(s)` deleted the suggestion from the
    // gallery + backend AT THE MOMENT of pick, so cancelling the Create
    // Chore pane meant the idea was gone forever. Retention rule: only
    // remove when the user actually SAVES a new chore that came from
    // this suggestion (handled by the parent `onPicked → save` flow)
    // OR when they explicitly X the card out. The parent Section wires
    // in-flight pick tracking so a successful save fires the remove.
    onPicked({
      _sourceSuggestion: { id: s.id, name: s.name },
      name: s.name, emoji: s.emoji, effort_hint: s.effort_hint,
      suggested_days: s.suggested_days, suggested_time_of_day: s.suggested_time_of_day,
    });
  };

  const toggleSelect = (s) => {
    // pt89Z — tap toggles selection. When >=1 selected, the sticky submit
    // bar handles the bulk-add path (with AI defaults). If a user wants
    // to fine-tune ONE card's dates/times right now, they can double-tap
    // or use the small chevron on the row (long-press on mobile is the
    // fallback route via the row's "Edit" button we render below).
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(s.id)) next.delete(s.id);
      else next.add(s.id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkAdd = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/calendar/${siloId}/chores/bulk-from-suggestions`, {
        suggestion_ids: ids,
      });
      const added = r.data?.added || [];
      const skipped = r.data?.skipped || [];
      // Server removes processed cards (both added + already-in-library
      // duplicates); mirror that locally by dropping every id we sent.
      const processedIds = new Set(ids);
      setSuggestions((cur) => cur.filter((c) => !processedIds.has(c.id)));
      clearSelection();
      if (added.length && skipped.length === 0) {
        toast.success(`Added ${added.length} chore${added.length === 1 ? "" : "s"}.`);
      } else if (added.length && skipped.length) {
        toast.success(`Added ${added.length}, skipped ${skipped.length} (already in library).`);
      } else if (!added.length && skipped.length) {
        toast(`All ${skipped.length} selected are already in your chore library.`, { id: "chore-bulk-all-dupe" });
      }
      onBulkAdded && onBulkAdded(added);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk add failed.");
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="absolute inset-0 bg-[#FDFCF8] rounded-2xl overflow-y-auto" data-testid="chore-suggestions-gallery">
      <div className="px-4 py-3 border-b hairline flex items-center justify-between sticky top-0 bg-[#FDFCF8] z-10">
        <h2 className="font-serif-display text-lg flex items-center gap-2"><Sparkle size={16} weight="duotone" className="text-[#C19A4A]" /> {galleryTitle}</h2>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#6B6A68] uppercase tracking-[0.14em]" data-testid="chore-gallery-count">
            {suggestions.length}/{CHORE_GALLERY_CAP}
          </span>
          <button onClick={suggestMore} disabled={busy || choreGalleryFull} data-testid="chore-suggest-more"
            title={choreGalleryFull
              ? `Gallery full (${CHORE_GALLERY_CAP}/${CHORE_GALLERY_CAP}). Delete a card to make room.`
              : `AI suggests chores for your ${householdMeta?.property_label || "household"} (uses your BYOK key)`}
            className="text-[10px] px-2.5 py-1.5 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-50 flex items-center gap-1">
            {busy ? <Spinner size={11} className="animate-spin" /> : <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" />} {suggestMoreLabel}
          </button>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="chore-gallery-close"
            title="Close the gallery and return to the New chore pane">
            <X size={16} />
          </button>
        </div>
      </div>
      {/* Feb 20, 2026 (pt89T — Randy) — Nudge input matching the ping
          gallery pattern. Blank ⇒ household-context driven. Non-blank ⇒
          every "Suggest more" batch biases toward this arena. */}
      <div className="px-4 pt-3 pb-2 border-b hairline bg-[#FBFAF6] sticky top-[45px] z-[9]">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5">
            <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" /> Nudge
            <span className="normal-case tracking-normal text-[#8A8A88] italic ml-auto">optional — biases every &ldquo;Suggest more&rdquo;</span>
          </span>
          <input type="text" value={nudge}
            onChange={(e) => setNudge(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) { e.preventDefault(); suggestMore(); } }}
            data-testid="chore-suggest-nudge"
            placeholder="e.g. welding shop, morning routine, dog grooming, kids soccer prep"
            maxLength={240}
            className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm focus:outline-none focus:border-[#4A5D4E]" />
        </label>
      </div>
      <div className="p-3 space-y-1.5 pb-24">
        {loading ? (
          <div className="text-xs text-[#6B6A68] italic p-2 flex items-center gap-1"><Spinner size={12} className="animate-spin" /> Loading…</div>
        ) : suggestions.length === 0 && !busy ? (
          <div className="text-xs text-[#6B6A68] italic p-2">
            {galleryEmpty}
          </div>
        ) : (
          suggestions.map((s) => {
            const isSelected = selected.has(s.id);
            return (
              <div key={s.id} className="relative group">
                <button onClick={() => toggleSelect(s)} data-testid={`chore-suggestion-${s.id}`}
                  aria-pressed={isSelected}
                  className={`w-full text-left flex items-center gap-2 p-2.5 pr-16 rounded-md border transition-colors ${
                    isSelected
                      ? "border-[#1C1B1A] border-2 bg-[#E8E4D8] ring-1 ring-[#1C1B1A]/20"
                      : "hairline hover:border-[#4A5D4E] hover:bg-[#F0F1EE]/50"
                  }`}>
                  <span className="text-xl">{s.emoji}</span>
                  <span className="flex-1">
                    <span className={`text-sm block ${isSelected ? "text-[#1C1B1A] font-medium" : "text-[#1C1B1A]"}`}>{s.name}</span>
                    <span className="text-[9px] text-[#6B6A68] uppercase tracking-[0.14em]">
                      {s.effort_hint} · {(s.suggested_days || []).length ? (s.suggested_days.map((d) => DOW[d]).join("·")) : "flexible"} · {s.suggested_time_of_day}
                    </span>
                  </span>
                  {isSelected && (
                    <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#1C1B1A] text-[#FDFCF8]"
                      data-testid={`chore-suggestion-selected-${s.id}`}>Picked</span>
                  )}
                </button>
                <button onClick={(e) => { e.stopPropagation(); setEditingSug(s); }}
                  data-testid={`chore-suggestion-details-${s.id}`}
                  title="Edit this idea's name, days, or time — stays in the gallery until you batch-add."
                  className="absolute top-1/2 -translate-y-1/2 right-8 p-1 rounded text-[9px] uppercase tracking-[0.14em] text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-white">
                  Edit
                </button>
                <button onClick={(e) => { e.stopPropagation(); remove(s); }}
                  data-testid={`chore-suggestion-remove-${s.id}`}
                  title="Remove this idea (Suggest more won't repeat it)"
                  className="absolute top-1/2 -translate-y-1/2 right-1.5 p-1 rounded-full text-[#B5B3AE] hover:text-[#C16E5A] hover:bg-white transition-colors">
                  <X size={13} weight="bold" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* pt89Z — Bulk-add sticky bar. Only renders when >=1 card is
          selected. Fixed to the bottom of the gallery pane; padded for
          iOS safe-area so it clears the home indicator on iPhone. */}
      {selected.size > 0 && (
        <div
          className="sticky bottom-0 left-0 right-0 z-20 bg-[#FDFCF8]/95 backdrop-blur-sm border-t hairline px-4 py-3 flex items-center gap-2"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
          data-testid="chore-bulk-add-bar">
          <span className="text-xs text-[#6B6A68]" data-testid="chore-bulk-count">
            {selected.size} selected
          </span>
          <span className="flex-1" />
          <button onClick={clearSelection} disabled={bulkBusy}
            data-testid="chore-bulk-clear"
            className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-50">
            Clear
          </button>
          <button onClick={bulkAdd} disabled={bulkBusy}
            data-testid="chore-bulk-add-btn"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {bulkBusy ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
            Add {selected.size} chore{selected.size === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {/* pt89AA — inline suggestion editor. Rendered as an overlay ON
          TOP of the gallery so gallery state + selection stay intact.
          Save PATCHes the card in-place; Cancel closes with no change. */}
      {editingSug && (
        <InlineSuggestionEditor
          siloId={siloId}
          suggestion={editingSug}
          onClose={() => setEditingSug(null)}
          onSaved={(updated) => {
            setSuggestions((cur) => cur.map((c) => c.id === updated.id ? { ...c, ...updated } : c));
            setEditingSug(null);
            toast.success("Idea updated.");
          }}
        />
      )}
    </div>
  );
}

// pt89AA — small inline editor for a single suggestion pill. Deliberately
// simple so it can live INSIDE the gallery without opening the heavy
// ChoreEditor modal (which commits to the official chore list on save).
function InlineSuggestionEditor({ siloId, suggestion, onClose, onSaved }) {
  const [name, setName] = useState(suggestion.name || "");
  const [emoji, setEmoji] = useState(suggestion.emoji || "🧹");
  const [effort, setEffort] = useState(suggestion.effort_hint || "medium");
  const [tod, setTod] = useState(suggestion.suggested_time_of_day || "anytime");
  const [days, setDays] = useState(new Set(suggestion.suggested_days || []));
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => {
    setDays((cur) => {
      const next = new Set(cur);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the idea a name."); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/calendar/${siloId}/chores/suggestions/${suggestion.id}`, {
        name: name.trim(),
        emoji: emoji.trim() || "🧹",
        effort_hint: effort,
        suggested_time_of_day: tod,
        suggested_days: Array.from(days).sort((a, b) => a - b),
      });
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="absolute inset-0 z-30 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose} data-testid="chore-suggestion-inline-editor">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFCF8] rounded-2xl w-full max-w-md shadow-2xl border hairline flex flex-col max-h-[86vh]">
        <div className="px-4 py-3 border-b hairline flex items-center gap-2">
          <div className="font-serif-display text-base flex-1">Edit idea</div>
          <button onClick={onClose} data-testid="chore-inline-close"
            className="p-1 text-[#6B6A68] hover:text-[#1C1B1A]" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-[auto_1fr] gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Emoji</span>
              <input value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
                data-testid="chore-inline-emoji"
                maxLength={4}
                className="mt-1 w-14 px-2 py-2 border hairline rounded-md bg-white text-lg text-center" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value.slice(0, 80))}
                data-testid="chore-inline-name"
                maxLength={80}
                className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Effort</span>
            <select value={effort} onChange={(e) => setEffort(e.target.value)}
              data-testid="chore-inline-effort"
              className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm">
              <option value="quick">Quick (a few minutes)</option>
              <option value="medium">Medium</option>
              <option value="long">Long</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Time of day</span>
            <select value={tod} onChange={(e) => setTod(e.target.value)}
              data-testid="chore-inline-tod"
              className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm">
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
              <option value="anytime">Anytime</option>
            </select>
          </label>
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] block mb-1.5">Days</span>
            <div className="flex flex-wrap gap-1.5" data-testid="chore-inline-days">
              {DOW.map((d, i) => {
                const on = days.has(i);
                return (
                  <button key={i} onClick={() => toggleDay(i)} type="button"
                    data-testid={`chore-inline-day-${i}`}
                    aria-pressed={on}
                    className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.14em] border transition-colors ${
                      on ? "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A]" : "bg-white text-[#6B6A68] hairline hover:border-[#4A5D4E]"
                    }`}>{d}</button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t hairline flex items-center gap-2">
          <span className="flex-1" />
          <button onClick={onClose} data-testid="chore-inline-cancel"
            className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE]">Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()}
            data-testid="chore-inline-save"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Spinner size={12} className="animate-spin" /> : null}Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChoresSection({ siloId, settings, onChanged }) {
  const [chores, setChores] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = settings?.household_meta || {};
  const [prop, setProp] = useState(meta.property || "");
  const [propExplain, setPropExplain] = useState(meta.property_explain || "");
  const [pets, setPets] = useState(meta.pets || "");
  const [petsExplain, setPetsExplain] = useState(meta.pets_explain || "");
  // `editor` shape: null | { …seed | …chore } | { _openGallery: true }
  const [editor, setEditor] = useState(null);
  const [dirty, setDirty] = useState(false);
  // Feb 22, 2026 (Randy pt89c-xvi) — collapsible master list. Once the
  // chore library fills up (Randy's East Fam has 41 entries and can hit
  // ~100), the section pushes everything else off-screen. Server-backed
  // (per-user, per-silo via /me/ui-prefs) so the collapse choice follows
  // the user across browsers/devices — not just this browser (Randy).
  const [listCollapsed, toggleListCollapsed] = useCollapsePref(siloId, "chores_list", false);
  // pt89Y — up to 3 user-added describers ({ slug, header, description }).
  // Simple shape, no options/emoji/AI-suggest.
  const describers = useMemo(() => Array.isArray(meta.describers) ? meta.describers.slice(0, 3) : [], [meta.describers]);
  const [describerModal, setDescriberModal] = useState(null); // null | "new" | { slug, header, description }

  // pt89Y — Property + Pets headers + options come from the Re-Themer.
  const tBox1Header  = useThemeLabel(T.THEME_KEY_CHORES_BOX1_HEADER, "Property");
  const tBox1OptsStr = useThemeLabel(T.THEME_KEY_CHORES_BOX1_OPTIONS);
  const tBox2Header  = useThemeLabel(T.THEME_KEY_CHORES_BOX2_HEADER, "Pets");
  const tBox2OptsStr = useThemeLabel(T.THEME_KEY_CHORES_BOX2_OPTIONS);
  const tExplainLbl  = useThemeLabel(T.THEME_KEY_CHORES_EXPLAIN_LABEL, "Explain (write your own)");
  const tAddDesc     = useThemeLabel(T.THEME_KEY_HOUSEHOLD_ADD_DESCRIBER, "New describer");
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep additions.
  const tSetupHeading = useThemeLabel(T.THEME_KEY_CHORES_SETUP_HEADING);
  const tRowLabel     = useThemeLabel(T.THEME_KEY_CHORES_ROW_LABEL);
  const tNewChore     = useThemeLabel(T.THEME_KEY_CHORES_NEW_BUTTON);
  const tSaveDetails  = useThemeLabel(T.THEME_KEY_CHORES_SAVE_DETAILS);
  const tSectionIntro = useThemeLabel(T.THEME_KEY_CHORES_SECTION_INTRO);
  const tEmptyState   = useThemeLabel(T.THEME_KEY_CHORES_EMPTY_STATE);
  const PROPERTY_OPTS = useMemo(() => parseOptionString(tBox1OptsStr, tExplainLbl), [tBox1OptsStr, tExplainLbl]);
  const PETS_OPTS     = useMemo(() => parseOptionString(tBox2OptsStr, tExplainLbl), [tBox2OptsStr, tExplainLbl]);

  useEffect(() => { setProp(meta.property || ""); setPropExplain(meta.property_explain || ""); setPets(meta.pets || ""); setPetsExplain(meta.pets_explain || ""); }, [meta.property, meta.property_explain, meta.pets, meta.pets_explain]);

  const saveDescriber = async (payload) => {
    // pt89Y — dedupe on slug, cap to 3.
    const existing = describers.filter((d) => d.slug !== payload.slug);
    const next = [...existing, {
      slug: payload.slug,
      header: payload.header,
      description: payload.description,
    }].slice(0, 3);
    await api.post(`/calendar/${siloId}/household-meta`, { describers: next });
    toast.success(`Saved "${payload.header}".`);
    onChanged && onChanged();
  };
  const removeDescriber = async (slug) => {
    const next = describers.filter((d) => d.slug !== slug);
    await api.post(`/calendar/${siloId}/household-meta`, { describers: next });
    toast.success("Describer removed.");
    onChanged && onChanged();
  };

  const load = async () => {
    try { const r = await api.get(`/calendar/${siloId}/chores`); setChores(r.data.chores || []); }
    catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siloId]);

  const saveMeta = async () => {
    try {
      await api.post(`/calendar/${siloId}/household-meta`, {
        property: prop || null,
        property_explain: prop === "explain" ? propExplain : null,
        pets: pets || null,
        pets_explain: pets === "explain" ? petsExplain : null,
      });
      toast.success("Household details saved.");
      setDirty(false);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };

  const togglePause = async (c) => {
    try { await api.patch(`/calendar/${siloId}/chores/${c.id}`, { active: !c.active }); load(); }
    catch { toast.error("Couldn't update."); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Kids with this chore assigned will stop seeing it.`)) return;
    try { await api.delete(`/calendar/${siloId}/chores/${c.id}`); load(); toast("Chore deleted."); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="chores-section" className="mt-1">
      <h3 className="font-serif-display text-base text-[#1C1B1A] mb-1.5" data-testid="chores-section-heading">{tSetupHeading}</h3>

      {/* pt89Y (Feb 20, 2026, Randy) — Chores row.
          Desktop: horizontal single row → [Chores label] [Property▼] [Pets▼]
                   [Desc1] [Desc2] [Desc3] [+ New Describer] · · · [+ New chore]
          Mobile: all 5 field controls STACK vertically (Property, Pets,
                  Desc1, Desc2, Desc3), then the two action buttons row
                  below. The `chores-field-strip` grid does the stacking. */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <Broom size={14} weight="duotone" className="text-[#4A5D4E]" />
        <span className="text-sm font-medium text-[#1C1B1A]">{tRowLabel}</span>
        <div className="chores-field-strip w-full sm:w-auto sm:flex sm:items-center sm:gap-2 grid grid-cols-1 gap-2 flex-1 min-w-0"
          data-testid="chores-field-strip">
          <select value={prop} onChange={(e) => { setProp(e.target.value); setDirty(true); }} data-testid="chores-property"
            title={`${tBox1Header} — feeds the AI Browse suggestions`}
            className="text-[11px] px-2 py-1 border hairline rounded bg-white w-full sm:w-auto sm:max-w-[180px]">
            <option value="">{tBox1Header}…</option>
            {PROPERTY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <select value={pets} onChange={(e) => { setPets(e.target.value); setDirty(true); }} data-testid="chores-pets"
            title={`${tBox2Header} — feeds the AI Browse suggestions`}
            className="text-[11px] px-2 py-1 border hairline rounded bg-white w-full sm:w-auto sm:max-w-[180px]">
            <option value="">{tBox2Header}…</option>
            {PETS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          {/* pt89Y — up to 3 describer chips. Same look/color as the two
              dropdowns to the left, minus the chevron. Click re-opens the
              simple modal in edit mode. */}
          {describers.map((d) => (
            <button key={d.slug} type="button" onClick={() => setDescriberModal(d)}
              data-testid={`chores-describer-chip-${d.slug}`}
              title={`Edit "${d.header}" — feeds the AI Browse suggestions`}
              className="text-[11px] px-2 py-1 border hairline rounded bg-white w-full sm:w-auto sm:max-w-[180px] text-left text-[#1C1B1A] hover:border-[#4A5D4E] truncate">
              {d.header}
            </button>
          ))}
          {describers.length < 3 && (
            <button type="button" onClick={() => setDescriberModal("new")}
              data-testid="chores-new-describer-btn"
              className="text-[10px] px-2 py-1 rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1 w-full sm:w-auto sm:ml-auto justify-center">
              <Plus size={11} weight="bold" /> {tAddDesc}
            </button>
          )}
        </div>
        <button onClick={() => setEditor({})} data-testid="chores-new-btn"
          className="text-[10px] px-2 py-1 rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1 w-full sm:w-auto justify-center">
          <Plus size={11} weight="bold" /> {tNewChore}
        </button>
      </div>
      {prop === "explain" && (
        <textarea value={propExplain} onChange={(e) => { setPropExplain(e.target.value); setDirty(true); }} rows={2}
          data-testid="chores-property-explain" placeholder="Describe your property (e.g., 3-bedroom townhouse with small backyard)"
          className="w-full mb-2 px-2 py-1.5 border hairline rounded-md text-xs bg-white" />
      )}
      {pets === "explain" && (
        <textarea value={petsExplain} onChange={(e) => { setPetsExplain(e.target.value); setDirty(true); }} rows={2}
          data-testid="chores-pets-explain" placeholder="Describe your pets (e.g., 2 dogs + 1 cat + backyard chickens)"
          className="w-full mb-2 px-2 py-1.5 border hairline rounded-md text-xs bg-white" />
      )}
      {dirty && (
        <div className="mb-2 flex justify-end">
          <button onClick={saveMeta} data-testid="chores-meta-save"
            className="text-[10px] px-2.5 py-1 rounded bg-[#4A5D4E] text-white">{tSaveDetails}</button>
        </div>
      )}
      <p className="text-[11px] text-[#6B6A68] mb-2 leading-relaxed">
        {tSectionIntro}
      </p>

      {loading ? (
        <div className="text-xs text-[#6B6A68] italic py-2">Loading…</div>
      ) : chores.length === 0 ? (
        <div className="text-[11px] text-[#6B6A68] italic p-3 border hairline rounded-md bg-white/60" data-testid="chores-empty">
          {tEmptyState}
        </div>
      ) : (
        <>
          {/* Feb 22, 2026 (Randy pt89c-xvi) — collapse toggle. Keeps the
              chore count visible and lets Randy hide the list when it's
              in the way of the buttons below it. State persists per silo. */}
          <button
            type="button"
            onClick={toggleListCollapsed}
            data-testid="chores-list-toggle"
            className="w-full mb-1.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] hover:text-[#1C1B1A] transition"
          >
            <span className="inline-block w-3 text-center">{listCollapsed ? "▶" : "▼"}</span>
            <span>
              {chores.length} chore{chores.length === 1 ? "" : "s"} in your library
              {chores.some((c) => !c.active) && (
                <span className="text-[#B5B3AE] normal-case tracking-normal ml-1">
                  · {chores.filter((c) => !c.active).length} paused
                </span>
              )}
            </span>
            <span className="flex-1 border-t hairline" />
            <span className="text-[9px] normal-case tracking-normal text-[#B5B3AE]">
              {listCollapsed ? "Show" : "Hide"}
            </span>
          </button>
          {!listCollapsed && (
            <div className="space-y-1.5" data-testid="chores-list-open">
              {chores.map((c) => (
                <div key={c.id} className={`flex items-center gap-2 bg-white border hairline rounded px-2.5 py-1.5 text-sm ${!c.active ? "opacity-55" : ""}`} data-testid={`chore-row-${c.id}`}>
              <span className="text-lg leading-none">{c.emoji}</span>
              <span className="flex-1 min-w-0">
                <span className="text-[#1C1B1A] truncate block">{c.name}</span>
                <span className="text-[9px] text-[#6B6A68] uppercase tracking-[0.14em]">
                  {c.effort_hint} · {(c.suggested_days || []).length ? (c.suggested_days.map((d) => DOW[d]).join("·")) : "flexible"} · {c.suggested_time_of_day}
                </span>
              </span>
              <button onClick={() => togglePause(c)} title={c.active ? "Pause" : "Resume"} data-testid={`chore-pause-${c.id}`} className="text-[#6B6A68] hover:text-[#4A5D4E]">
                {c.active ? <Pause size={13} /> : <Play size={13} />}
              </button>
              <button onClick={() => setEditor(c)} data-testid={`chore-edit-${c.id}`} className="text-[#6B6A68] hover:text-[#4A5D4E]"><PencilSimple size={13} /></button>
              <button onClick={() => del(c)} data-testid={`chore-delete-${c.id}`} className="text-[#6B6A68] hover:text-[#C16E5A]"><Trash size={13} /></button>
            </div>
          ))}
            </div>
          )}
        </>
      )}

      {editor && (
        <ChoreEditor
          siloId={siloId}
          householdMeta={{ ...meta, property_label: PROPERTY_OPTS.find((o) => o.v === prop)?.l }}
          editing={editor?.id ? editor : (editor?._openGallery ? null : editor)}
          autoOpenGallery={!!editor?._openGallery}
          onClose={() => setEditor(null)}
          onSaved={load}
        />
      )}

      {/* pt89Y — Simple describer popup. Opens on "+ New Describer" (create
          mode) or on any existing chip click (edit mode with pre-filled
          fields + Remove button). */}
      <SimpleDescriberModal
        open={!!describerModal}
        initial={describerModal === "new" ? null : describerModal}
        existingCount={describers.length}
        onClose={() => setDescriberModal(null)}
        onSave={saveDescriber}
        onDelete={removeDescriber}
      />
    </div>
  );
}
